import * as ng from 'angular';
import { InformationNodeObject, OrganismBillingPaymentOptionsController } from '@/atomic-components';
import {
    AuthContextService,
    DepositModelService,
    UtilHelperService,
    SentryErrorEmitterService,
    AlertManagerService
} from '@/services';
import * as Types from '@/types';
import { Severity } from '@sentry/browser';

export interface PaymentMethodOption {
    type: string;
    label: string;
}

export class OrganEditPanelBillingPaymentMethodController {
    public static $inject: string[] = ['$translate', 'depositModel', 'alertManager'];

    public cancel: () => void;
    public $editFormOrganism: OrganismBillingPaymentOptionsController;
    public panelRight: unknown;
    public paymentMethods: PaymentMethodOption[] = [];
    public blockPaymentChange: boolean;
    public initialPaymentType: string;
    public latestBankAccount: Types.BillingApi.BankAccount;
    public parentInformation: Types.AccountApi.AccountOrganizationInformation;
    public prepaidIsAvailable = false;
    public paymentNoticeForGovernmentInstitutions: InformationNodeObject;
    public callbackLoading = false;

    private _pendingSepaVerification = false;

    constructor(
        private $translate: ng.translate.ITranslateService,
        private depositModel: DepositModelService,
        private alertManager: AlertManagerService,
    ) {
        this.paymentNoticeForGovernmentInstitutions = {
            headline: this.$translate.instant('TR_030321-523a0f_TR'),
            text: [this.$translate.instant('TR_030321-5f427b_TR')]
        };
    }

    public $onInit(): void {
        this.parentInformation = AuthContextService.accountOrganizationInformation;

        void this.depositModel.paymentMethodAvailability().then(
            (paymentMethodAvailability: {paymentMethod: string; available: boolean}[]) => {
                const availableMethods = paymentMethodAvailability
                    .filter((method) => method.available)
                    .map((method) => method.paymentMethod);

                if (availableMethods.some(
                    (method) => ['stripe', 'paypal'].indexOf(method.toLowerCase()) >= 0
                )) {
                    this.paymentMethods = [{type: 'prepaid', label: this.$translate.instant('TR_090119-296e1a_TR')}];
                    this.prepaidIsAvailable = true;
                }

                if (availableMethods.some(
                    (method) => ['directdebit'].indexOf(method.toLowerCase()) >= 0
                )) {
                    this.paymentMethods.push({type: 'postpaid', label: this.$translate.instant('TR_090119-e5076a_TR')});
                }

                if (this.$editFormOrganism.paymentType === 'invoice') {
                    this.paymentMethods.push({type: 'invoice', label: this.$translate.instant('TR_060819-c8af3e_TR')});
                }

                if (availableMethods.some(
                    (method) => ['postpaidstripe'].indexOf(method.toLowerCase()) >= 0
                )) {
                    this.paymentMethods.push(
                        { type: 'card', label: this.$translate.instant('TR_090119-aa221e_TR')},
                        { type: 'paypal', label: this.$translate.instant('TR_090119-efbad6_TR')},
                        { type: 'ideal', label: this.$translate.instant('TR_010925-cbbc42_TR')},
                        { type: 'bancontact', label: this.$translate.instant('TR_010925-5df562_TR')},
                        { type: 'amazonPay', label: this.$translate.instant('TR_010925-7024ee_TR')},
                    );
                }
            }
        );

        if ([undefined].indexOf(this.blockPaymentChange) >= 0) {
            this.blockPaymentChange = false;
        }

        this.initialPaymentType = ng.copy(this.$editFormOrganism.paymentType);
    }

    public get isNoInvoice(): boolean {
        return AuthContextService.account.billingSettings.postpaidType === 'noInvoice';
    }

    public get selectedPaymentMethod(): string {
        if (this.isNoInvoice) {
            return this.$translate.instant('TR_230626-aabe59_TR');
        }

        if (this.$editFormOrganism.paymentType === 'postpaid') {
            return this.$translate.instant('TR_090119-e5076a_TR');
        } else if (this.$editFormOrganism.paymentType === 'invoice') {
            return this.$translate.instant('TR_060819-c8af3e_TR');
        } else if (this.$editFormOrganism.paymentType === 'prepaid') {
            return this.$translate.instant('TR_090119-296e1a_TR');
        } else if (this.isPaymentAccount) {
            return this.$editFormOrganism.paymentAccountDisplayName;
        } else {
            return this.$editFormOrganism.paymentType;
        }
    }

    public get showSwitchFromPostToPrepaidWarning(): boolean {
        return (this.initialPaymentType === 'postpaid' || this.initialPaymentType === 'paymentAccount')
            && this.$editFormOrganism.paymentType === 'prepaid'
            && this.prepaidIsAvailable;
    }

    public get showPrePaidInfoPanel(): boolean {
        return this.$editFormOrganism.paymentType === 'prepaid'
            && this.parentInformation !== undefined
            && [undefined, null, ''].indexOf(AuthContextService.account.customerNumber) < 0;
    }

    public set pendingSepaVerification({}) {}
    public get pendingSepaVerification(): boolean {
        return this._pendingSepaVerification;
    }

    public get disableSaveButton(): boolean {
        return this.isPaymentAccount;
    }

    public get isPaymentAccount(): boolean {
        return ['card', 'paypal', 'ideal', 'bancontact', 'amazonPay'].indexOf(this.$editFormOrganism.paymentType) >= 0;
    }

    public get hasPrepaid(): boolean {
        return this.paymentMethods.some((item: PaymentMethodOption) => item.type === 'prepaid');
    }

    public get hasSepa(): boolean {
        return this.paymentMethods.some((item: PaymentMethodOption) => item.type === 'postpaid');
    }

    public get hasPaymentAccount(): boolean {
        return this.paymentMethods.some((item: PaymentMethodOption) => item.type === 'card');
    }

    public setupPaymentAccount = () => {
        this.callbackLoading = true;
        void this.depositModel.createPaymentAccount(this.$editFormOrganism.paymentType).then(
            (result: Types.UI.APISingleResponse<Types.BillingApi.StripePaymentStartResult>): any => {
                if (result.response.paymentPublicKey.length > 0) {
                    const stripe = Stripe(result.response.paymentPublicKey); // eslint-disable-line new-cap
                    return void stripe.redirectToCheckout({
                        sessionId: result.response.externalPaymentSession,
                    }).catch(this.handleCallbackError);
                }
            },
        ).catch(this.handleCallbackError);
    }

    // already used in app/components/account-activation/account-activation-template.ts
    // TODO: Refactor when refactor whole component
    public getIgnoredErrors = () => {
        return {
            20005: /* translationId */ 'ERRORS.20005',
            62055: /* translationId */ 'TR_230119-81c8a2_TR',
            62056: /* translationId */ 'TR_230119-cfe94a_TR',
            62057: /* translationId */ 'TR_230119-a19bab_TR',
            62058: /* translationId */ 'TR_230119-58aa69_TR',
            62060: /* translationId */ 'TR_230119-aa192d_TR',
            62061: /* translationId */ 'TR_230119-1a2625_TR',
            62062: /* translationId */ 'TR_230119-d6f3a0_TR',
            62063: /* translationId */ 'TR_230119-922dc6_TR',
        }
    }

    // already used in app/components/account-activation/account-activation-template.ts
    // TODO: Refactor when refactor whole component
    public handleCallbackError = (err: string | Error) => {
        let ignoreSentry = false;

        // eslint-disable-next-line
        if (UtilHelperService.checkForProperty(err, 'code') && UtilHelperService.checkForProperty(this.getIgnoredErrors(), (err as any).code)) {
            ignoreSentry = true;
        }

        let message: string;
        switch (true) {
            case typeof err === 'string':
                message = err;
                break;
            case [null, undefined].indexOf(err) >= 0:
                message = 'undefined error';
                break;
            case UtilHelperService.checkForProperty(err, 'message'):
                message = err.message;
                break;

            default:
                try {
                    message = err.toString();
                } catch {
                    message = this.$translate.instant('TR_030725-cea024_TR');
                }
                break;
        }

        if (!ignoreSentry) {
            SentryErrorEmitterService.sendSentryReport(err, null, { paymentAccount: 'callback' }, Severity.Error);
        }
        this.alertManager.error(message, null, null, null, true);

        setTimeout(() => {
            this.callbackLoading = false;
        })
    }
}

export class OrganEditPanelBillingPaymentMethodComponent implements ng.IComponentOptions {
    public bindings = {
        _pendingSepaVerification: '<?pendingSepaVerification',
        blockPaymentChange: '<?',
        cancel: '<',
        latestBankAccount: '<',
        panelRight: '<'
    };
    public require = {
        $editFormOrganism: '^organismBillingPaymentOptions'
    };
    public controllerAs = '$editPanelOrgan';
    public controller = OrganEditPanelBillingPaymentMethodController;
    public template = require('./payment-method.html');
}
